import React, { useState } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import {
  Container,
  Typography,
  Paper,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Chip,
  Fade,
  Zoom
} from '@material-ui/core'
import {
  ExpandMore,
  Work,
  AttachMoney,
  Schedule,
  LocationOn,
  FlightTakeoff,
  School,
  Visibility,
  VisibilityOff
} from '@material-ui/icons'

const useStyles = makeStyles((theme) => ({
  hrfaqContainer: {
    background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.95) 0%, rgba(20, 0, 40, 0.98) 50%, rgba(0, 20, 40, 0.95) 100%)',
    minHeight: '100vh',
    padding: theme.spacing(4, 0),
    position: 'relative',
    overflow: 'hidden',
    paddingTop: '5.5rem',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: `
        repeating-linear-gradient(
          0deg,
          transparent,
          transparent 2px,
          rgba(0, 255, 255, 0.03) 2px,
          rgba(0, 255, 255, 0.03) 4px
        )
      `,
      pointerEvents: 'none',
    }
  },
  headerSection: {
    background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.8) 0%, rgba(10, 0, 20, 0.9) 50%, rgba(0, 10, 20, 0.8) 100%)',
    border: '1px solid rgba(0, 255, 255, 0.4)',
    borderRadius: '16px',
    padding: theme.spacing(4),
    marginBottom: theme.spacing(4),
    position: 'relative',
    transition: 'all 0.3s ease',
    backdropFilter: 'blur(15px)',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
    '&:hover': {
      border: '1px solid rgba(0, 255, 255, 0.6)',
      boxShadow: '0 12px 40px rgba(0, 255, 255, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
    }
  },
  title: {
    fontFamily: '"Share Tech Mono", monospace',
    fontSize: '2.5rem',
    fontWeight: 'bold',
    background: 'linear-gradient(45deg, #00ffff 30%, #ff00ff 90%)',
    backgroundClip: 'text',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    textAlign: 'center',
    marginBottom: theme.spacing(2),
    textShadow: '0 0 20px rgba(0, 255, 255, 0.5)',
  },
  subtitle: {
    fontFamily: '"Inter", "Roboto", sans-serif',
    fontSize: '1.2rem',
    color: '#bbb',
    textAlign: 'center',
    lineHeight: 1.6,
  },
  faqAccordion: {
    background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.8) 0%, rgba(10, 0, 20, 0.9) 50%, rgba(0, 10, 20, 0.8) 100%)',
    border: '1px solid rgba(255, 0, 255, 0.4)',
    borderRadius: '12px !important',
    margin: theme.spacing(2, 0),
    transition: 'all 0.3s ease',
    backdropFilter: 'blur(10px)',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
    '&:hover': {
      border: '1px solid rgba(0, 255, 255, 0.8)',
      boxShadow: '0 12px 40px rgba(0, 255, 255, 0.2)',
      transform: 'translateY(-2px)',
    },
    '&.Mui-expanded': {
      border: '1px solid rgba(0, 255, 255, 0.8)',
      boxShadow: '0 12px 40px rgba(0, 255, 255, 0.3)',
    },
    '&::before': {
      display: 'none',
    }
  },
  accordionSummary: {
    padding: theme.spacing(2, 3),
    '& .MuiAccordionSummary-content': {
      alignItems: 'center',
      gap: theme.spacing(2),
    },
    '& .MuiAccordionSummary-expandIcon': {
      color: '#00ffff',
    }
  },
  questionText: {
    fontFamily: '"Share Tech Mono", monospace',
    fontSize: '1.1rem',
    color: '#00ffff',
    fontWeight: 'bold',
  },
  questionIcon: {
    color: '#ff00ff',
    fontSize: '1.5rem',
  },
  accordionDetails: {
    padding: theme.spacing(0, 3, 3, 3),
    borderTop: '1px solid rgba(0, 255, 255, 0.2)',
  },
  answerText: {
    fontFamily: '"Inter", "Roboto", sans-serif',
    fontSize: '1rem',
    color: '#e0e0e0',
    lineHeight: 1.6,
    marginTop: theme.spacing(1),
  },
  statusChip: {
    background: 'linear-gradient(45deg, #00ff00 30%, #00cc00 90%)',
    color: '#000',
    fontFamily: '"Share Tech Mono", monospace',
    fontWeight: 'bold',
    marginTop: theme.spacing(1),
  },
  salaryRevealContainer: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    border: '1px solid rgba(255, 0, 255, 0.4)',
    borderRadius: '8px',
    background: 'rgba(0, 0, 0, 0.3)',
    position: 'relative',
  },
  revealButton: {
    background: 'linear-gradient(45deg, #ff00ff 30%, #ff0080 90%)',
    color: '#fff',
    fontFamily: '"Share Tech Mono", monospace',
    fontWeight: 'bold',
    padding: theme.spacing(1, 3),
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    boxShadow: '0 4px 15px rgba(255, 0, 255, 0.3)',
    '&:hover': {
      background: 'linear-gradient(45deg, #ff0080 30%, #ff00ff 90%)',
      boxShadow: '0 6px 20px rgba(255, 0, 255, 0.5)',
      transform: 'translateY(-2px)',
    }
  },
  salaryText: {
    fontFamily: '"Share Tech Mono", monospace',
    fontSize: '1.2rem',
    color: '#00ff00',
    fontWeight: 'bold',
    textShadow: '0 0 10px rgba(0, 255, 0, 0.5)',
    marginTop: theme.spacing(1),
  },
  hiddenSalary: {
    fontFamily: '"Share Tech Mono", monospace',
    fontSize: '1.2rem',
    color: '#666',
    letterSpacing: '2px',
  },
  '@keyframes fadeInUp': {
    '0%': {
      opacity: 0,
      transform: 'translateY(30px)',
    },
    '100%': {
      opacity: 1,
      transform: 'translateY(0)',
    }
  },
  '@keyframes glowPulse': {
    '0%': {
      boxShadow: '0 0 5px rgba(0, 255, 255, 0.5)',
    },
    '50%': {
      boxShadow: '0 0 20px rgba(0, 255, 255, 0.8)',
    },
    '100%': {
      boxShadow: '0 0 5px rgba(0, 255, 255, 0.5)',
    }
  }
}))

const CyberpunkHRFAQ = () => {
  const classes = useStyles()
  const [salaryRevealed, setSalaryRevealed] = useState(false)
  const [expandedPanel, setExpandedPanel] = useState(false)

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpandedPanel(isExpanded ? panel : false)
  }

  const faqData = [
    {
      id: 'work-auth',
      icon: <Work className={classes.questionIcon}/>,
      question: 'What is your work authorization status? Do you need sponsorship?',
      answer: 'I am a United States citizen and do not require any work authorization or visa sponsorship.',
      status: 'US Citizen',
      statusColor: '#00ff00'
    },
    {
      id: 'salary',
      icon: <AttachMoney className={classes.questionIcon}/>,
      question: 'What are your salary expectations?',
      answer: 'My compensation expectations are competitive with senior engineering roles in the market.',
      hasReveal: true
    },
    {
      id: 'availability',
      icon: <Schedule className={classes.questionIcon}/>,
      question: 'What is your availability and notice period?',
      answer: 'I am currently available and can start within 2-4 weeks notice, depending on project transition requirements.',
      status: 'Available',
      statusColor: '#00ff00'
    },
    {
      id: 'location',
      icon: <LocationOn className={classes.questionIcon}/>,
      question: 'Are you open to onsite/hybrid/remote work? Are you willing to relocate?',
      answer: 'I am open to onsite/hybrid/remote work. My location is Austin, Texas. I work in Central Time. Unfortunately I am not able to relocate.',
      status: 'Flexible',
      statusColor: '#00ffff'
    },
    {
      id: 'travel',
      icon: <FlightTakeoff className={classes.questionIcon}/>,
      question: 'Are you willing to travel for work?',
      answer: 'Yes, I am willing to travel for business needs, client meetings, conferences, and team collaboration. I understand that senior roles may require occasional travel.',
      status: 'Yes',
      statusColor: '#00ff00'
    },
    {
      id: 'background',
      icon: <School className={classes.questionIcon}/>,
      question: 'Are you comfortable with background checks and drug screening?',
      answer: 'Yes, I am comfortable with standard background checks, reference checks, and drug screening as required by company policy.',
      status: 'No Issues',
      statusColor: '#00ff00'
    }
  ]

  const renderSalaryReveal = () => (
    <Box className={classes.salaryRevealContainer}>
      {!salaryRevealed ? (
        <Box>
          <Typography className={classes.hiddenSalary}>
            ████████████████████████
          </Typography>
          <Button
            className={classes.revealButton}
            onClick={() => setSalaryRevealed(true)}
            startIcon={<Visibility/>}
          >
            [DECRYPT_SALARY_DATA]
          </Button>
        </Box>
      ) : (
        <Fade in={salaryRevealed} timeout={800}>
          <Box>
            <Typography className={classes.salaryText}>
              💰 $230,000 - $250,000 Base Salary + Equity
            </Typography>
            <Typography style={{
              color: '#888',
              fontSize: '0.9rem',
              marginTop: '8px',
              fontFamily: '"Inter", "Roboto", sans-serif'
            }}>
              Open to negotiation based on total compensation package, equity, benefits, and growth opportunities.
            </Typography>
          </Box>
        </Fade>
      )}
    </Box>
  )

  return (
    <Box className={classes.hrfaqContainer}>
      <Container maxWidth="md">
        <Fade in={true} timeout={1000}>
          <Paper className={classes.headerSection}>
            <Typography className={classes.title}>
              [HR_FAQ]
            </Typography>
            <Typography className={classes.subtitle}>
              Frequently Asked Questions • Recruiter Information • Employment Details
            </Typography>
          </Paper>
        </Fade>

        {faqData.map((faq, index) => (
          <Zoom in={true} style={{ transitionDelay: `${index * 150}ms` }} key={faq.id}>
            <Accordion
              className={classes.faqAccordion}
              expanded={expandedPanel === faq.id}
              onChange={handleAccordionChange(faq.id)}
            >
              <AccordionSummary
                className={classes.accordionSummary}
                expandIcon={<ExpandMore/>}
              >
                {faq.icon}
                <Typography className={classes.questionText}>
                  {faq.question}
                </Typography>
              </AccordionSummary>
              <AccordionDetails className={classes.accordionDetails}>
                <Box>
                  <Typography className={classes.answerText}>
                    {faq.answer}
                  </Typography>

                  {faq.status && (
                    <Chip
                      label={faq.status}
                      className={classes.statusChip}
                      size="small"
                    />
                  )}

                  {faq.hasReveal && renderSalaryReveal()}
                </Box>
              </AccordionDetails>
            </Accordion>
          </Zoom>
        ))}
      </Container>
    </Box>
  )
}

export default CyberpunkHRFAQ
